import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import useSessionPersistedState from '../../../hooks/useSessionPersistedState';

const DEFAULT_PAGINATION = { page: 0, size: 10 };

const useSupplierFilters = ({ defaultQuery, searchOnChange = true, persistKey } = {}) => {
  const [selectedFilter, setSelectedFilter] = useSessionPersistedState(
    persistKey ? `${persistKey}:selectedFilter` : undefined,
    'name',
  );
  const [pagination, setPagination] = useSessionPersistedState(
    persistKey ? `${persistKey}:pagination` : undefined,
    DEFAULT_PAGINATION,
  );
  const [searchTerm, setSearchTerm] = useSessionPersistedState(persistKey ? `${persistKey}:searchTerm` : undefined, '');

  const skipFilterPageReset = useRef(true);
  const skipSearchPageReset = useRef(true);

  const buildQuery = () => {
    const query = { ...defaultQuery, ...pagination };
    if (searchTerm) {
      query[selectedFilter] = searchTerm;
    }

    return query;
  };

  const [currentQuery, setCurrentQuery] = useState(() => buildQuery());

  useEffect(() => {
    if (skipFilterPageReset.current) {
      skipFilterPageReset.current = false;
      return;
    }
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [selectedFilter, setPagination]);

  useEffect(() => {
    setCurrentQuery(buildQuery());
  }, [selectedFilter, pagination]);

  useEffect(() => {
    if (!searchOnChange) return;

    const handler = debounce(() => {
      setCurrentQuery(buildQuery());
      if (skipSearchPageReset.current) {
        skipSearchPageReset.current = false;
        return;
      }
      setPagination((prev) => ({ ...prev, page: 0 }));
    }, 500);

    handler();
    return () => handler.cancel();
  }, [searchTerm]);

  const handleFilterChange = (event) => setSelectedFilter(event.target.value);

  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  const handlePaginationChange = (page, limit) => setPagination({ page: page, size: limit });

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      setCurrentQuery(buildQuery());
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  return {
    searchTerm,
    selectedFilter,
    currentQuery,
    handleFilterChange,
    handleSearchChange,
    handleSearchKeyPress,
    handlePaginationChange,
  };
};

export default useSupplierFilters;
